import { useState, useEffect, useRef, useMemo } from 'react';
import { cartService } from '@/features/cart/services/cartService';
import type { CartResponse, CartConflictReport, CartItemStatus } from '@/features/cart/types';
import { CartStatus, ItemStatusCode } from '@/features/cart/types';

export interface UseCartConflictsReturn {
  hasConflict: boolean;
  availabilityConflicts: Record<string, string[]>;
  addOnConflicts: CartConflictReport['add_on_conflicts'];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export function useCartConflicts(
  cart: CartResponse | null,
  itemStatuses: Record<string, CartItemStatus | null>
): UseCartConflictsReturn {
  const [conflictReport, setConflictReport] = useState<CartConflictReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const conflictFetchingRef = useRef(false);

  // Memoize cart ID and items to create stable dependencies
  const cartId = cart?.id;
  const itemIdsString = useMemo(() => {
    if (!cart?.items || cart.items.length === 0) return '';
    return cart.items.map(item => item.id).sort().join(',');
  }, [cart?.items]);

  // Check if we should fetch conflict report (immediately for ACTIVE carts)
  const shouldFetchConflicts = useMemo((): boolean => {
    // Only fetch conflicts for ACTIVE carts
    if (!cart || cart.status !== CartStatus.Active) {
      return false;
    }

    // If no items, no conflicts to check
    if (!cart.items || cart.items.length === 0) {
      return false;
    }

    return true;
  }, [cart]);

  // Check if all item statuses are loaded and all are GOOD_FOR_BOOKING (for showing conflicts)
  const shouldShowConflicts = useMemo((): boolean => {
    if (!cart || cart.status !== CartStatus.Active) {
      return false;
    }

    if (!cart.items || cart.items.length === 0) {
      return false;
    }

    // Check if all statuses are loaded (no null values)
    const allStatusesLoaded = cart.items.every(item => itemStatuses[item.id] !== null);
    if (!allStatusesLoaded) {
      return false;
    }

    // Check if all items are good for booking
    const allGoodForBooking = cart.items.every(item => 
      itemStatuses[item.id]?.item_status_code === ItemStatusCode.GoodForBooking
    );

    return allGoodForBooking;
  }, [cart, itemStatuses]);

  // Fetch conflict report immediately when cart is ACTIVE
  const fetchConflictReport = async () => {
    if (!cartId || !shouldFetchConflicts || conflictFetchingRef.current) {
      return;
    }

    conflictFetchingRef.current = true;
    setIsLoading(true);
    try {
      const report = await cartService.getCartConflictReport(cartId);
      setConflictReport(report);
    } catch (error) {
      console.error('Failed to fetch conflict report:', error);
      // On error, assume no conflicts to avoid blocking user
      setConflictReport({
        has_conflict: false,
        availability_conflicts: {},
        add_on_conflicts: []
      });
    } finally {
      setIsLoading(false);
      conflictFetchingRef.current = false;
    }
  };

  // Fetch conflict report immediately when cart is ACTIVE (before status endpoints)
  useEffect(() => {
    if (shouldFetchConflicts && cartId) {
      fetchConflictReport();
    } else {
      // Reset conflict report when cart is not ACTIVE
      setConflictReport(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchConflicts, cartId, itemIdsString]); // itemIdsString changes when items change

  // Only return conflicts if all statuses are GOOD_FOR_BOOKING (don't show conflicts until then)
  const shouldUseConflicts = shouldShowConflicts && conflictReport !== null;

  // Return conflict state (only show if all statuses are GOOD_FOR_BOOKING)
  return {
    hasConflict: shouldUseConflicts ? (conflictReport?.has_conflict ?? false) : false,
    availabilityConflicts: shouldUseConflicts ? (conflictReport?.availability_conflicts ?? {}) : {},
    addOnConflicts: shouldUseConflicts ? (conflictReport?.add_on_conflicts ?? []) : [],
    isLoading,
    refetch: fetchConflictReport,
  };
}

